import {
    Button,
    Form,
    InputControl,
    Picker,
    RequiredText,
    StyleFunction,
    useThemedStyle,
} from "@venuepos/react-common";
import React, { View } from "react-native";
import { AvailableLocale, useTranslation } from "locales";
import {
    GQFiskalyEstablishmentsQuery,
    GQGetFiskalyTaxPayerQuery,
} from "graphql-sdk";
import { FiskalyEstablishmentSubmissionForm } from "../fiskaly-form";
import { formatDate } from "lib";
import { useAdminSession } from "../../../../session";

export function SubmissionForm({
    form,
    establishments,
    taxpayer,
    onSubmit,
    submitButton,
}: {
    form: Form<FiskalyEstablishmentSubmissionForm>;
    establishments: GQFiskalyEstablishmentsQuery;
    taxpayer: GQGetFiskalyTaxPayerQuery;
    onSubmit: () => void;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    const items = establishments?.fiskalyEstablishments.data
        .map(itr => {
            const submitDateString = !itr.submitBefore
                ? ""
                : `, ${t(
                      "backoffice.integration.fiskaly.submit_before_date",
                      "submit before {{ date }}",
                      {
                          date: formatDate(
                              itr.submitBefore,
                              locale as AvailableLocale
                          ),
                          interpolation: { escapeValue: false },
                      }
                  )}`;

            return {
                id: itr.id,
                name: itr.designation,
                address: `${itr.designation} (${itr.street} ${itr.houseNumber}${itr.houseNumberSuffix}, ${itr.zip} ${itr.town})${submitDateString}`,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <View style={styles.container}>
            <InputControl
                error={errors.establishmentId}
                description={t(
                    "backoffice.integration.fiskaly.select_establishment_description",
                    "Select an establishment to submit for"
                )}
            >
                <Picker
                    required
                    label={t(
                        "backoffice.integration.fiskaly.establishments",
                        "Establishments"
                    )}
                    onValueChange={newValue => {
                        setValue("establishmentId", newValue);
                    }}
                    selectedValue={values.establishmentId}
                    disabled={!items || items?.length === 0}
                >
                    <Picker.Item
                        label={t("backoffice.common.select", "Select an item")}
                        value=""
                        key="__select__"
                    />
                    {items?.map(itr => (
                        <Picker.Item
                            value={itr.id}
                            label={itr.address}
                            key={itr.id}
                        />
                    ))}
                </Picker>
            </InputControl>
            <Button
                onPress={handleSubmit(onSubmit)}
                size="small"
                disabled={items?.length <= 0 || !taxpayer?.getFiskalyTaxPayer}
            >
                {t(submitButton[0], submitButton[1])}
            </Button>
            <RequiredText />
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: { maxWidth: theme.dimensions.maxFormWidth },
});
